/**
 * 재고관리 → [제조] 탭 — 제조신청(대기·반려) + 제조 기록(완료·분해).
 *
 * 표가 둘인 이유: 하는 일이 다르다.
 *   위  = 아직 재고가 안 움직인 **신청** — 승인권자가 처리해야 할 일감
 *   아래 = 이미 재고가 움직인 **기록** — 원가 근거 조회·분해
 *
 * 행을 누르면:
 *   신청(대기)   → 승인권자는 [승인/반려] 화면, 신청자 본인은 [수정/삭제] 화면
 *   신청(승인됨) → 그 신청으로 만들어진 제조 상세
 *   제조 기록    → 투입 원재료 명세 (완제품이 재고에 그대로면 [분해] 가능)
 */

import { lazy, Suspense, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as manufactureApi from '../lib/manufacture-api';
import * as requestApi from '../lib/manufacture-request-api';
import type { ManufactureRequestRow, ManufactureRow } from '../lib/types';
import { SearchBar } from './SearchBar';
import { useAuth } from '../lib/auth-context';
import { LoadingLines } from './ui/Skeleton';

const ManufactureFormModal = lazy(() =>
  import('./ManufactureFormModal').then((m) => ({ default: m.ManufactureFormModal })),
);
const ManufactureDetailModal = lazy(() =>
  import('./ManufactureDetailModal').then((m) => ({ default: m.ManufactureDetailModal })),
);

const money = (n: number | null | undefined) =>
  n == null ? '' : Math.round(n).toLocaleString('ko-KR');

/** 신청 완제품 표시명 — 기존 품목이면 품목명, 새 품목이면 신청서에 적은 이름. */
function requestProductLabel(r: ManufactureRequestRow): string {
  return (
    r.productItemName ??
    r.newItem?.itemName ??
    r.productItemCode ??
    r.newItem?.itemCode ??
    '(완제품)'
  );
}

export function ManufactureTab() {
  const { user } = useAuth();
  // 제조 실행·분해·승인 = 출고검수 승인자와 같은 범위 (2026-07-28).
  const canApprove = user?.role === 'mgmt' || user?.role === 'admin';
  // viewer 는 매입 원가를 못 본다 (매입관리·보유재고 자산과 같은 규칙) → 원가 칼럼 자체를 숨긴다.
  const isViewer = user?.role === 'viewer';
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  /** 신청 화면을 열 때 — 승인 모드인지 수정 모드인지 함께 들고 다닌다 */
  const [openRequest, setOpenRequest] = useState<{
    row: ManufactureRequestRow;
    mode: 'approve' | 'request';
  } | null>(null);

  const listQ = useQuery({
    queryKey: ['manufactures', q],
    queryFn: () => manufactureApi.listManufactures({ q }),
    staleTime: 30_000,
  });
  const rows: ManufactureRow[] = listQ.data ?? [];

  const reqQ = useQuery({
    queryKey: ['manufacture-requests', q],
    queryFn: () => requestApi.listRequests({ q }),
    staleTime: 30_000,
  });
  // 위 표는 '처리할 일감'만 — 승인된 신청은 아래 제조 기록에 이미 나온다.
  const pendingRequests: ManufactureRequestRow[] = (reqQ.data ?? []).filter(
    (r) => r.status !== '승인',
  );
  const waitingCount = pendingRequests.filter((r) => r.status === '신청').length;

  function handleRequestClick(r: ManufactureRequestRow) {
    if (r.status === '승인' && r.manufactureId != null) {
      setDetailId(r.manufactureId);
      return;
    }
    if (canApprove && r.status === '신청') {
      setOpenRequest({ row: r, mode: 'approve' });
      return;
    }
    if (canApprove || r.requesterUserId === user?.userId) {
      setOpenRequest({ row: r, mode: 'request' });
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <SearchBar
          value={q}
          onCommit={setQ}
          live
          placeholder="🔍 제조번호 · 신청번호 · 품목 · 비고"
        />
        <div className="flex items-center gap-2">
          {/* 승인권자는 신청 없이 그 자리에서 처리, 그 외에는 신청 */}
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="btn btn-primary"
          >
            🔧 {canApprove ? '제조 처리' : '제조 신청'}
          </button>
          {canApprove && waitingCount > 0 && (
            <span className="text-xs px-2 py-1 rounded bg-rose-50 text-rose-700 border border-rose-200 whitespace-nowrap">
              승인 대기 {waitingCount}건
            </span>
          )}
        </div>
      </div>

      <div className="text-xs text-slate-500 mb-2 leading-relaxed">
        자재를 조립해 새 완제품을 만드는 기능입니다. 제조하면 <strong>원재료는 재고에서 빠지고</strong>{' '}
        완제품이 <strong>재고에 들어오며</strong>, 원재료 원가가 완제품 원가로 합산되어 나중에
        출고할 때 그대로 따라갑니다.{' '}
        {canApprove ? (
          <>
            현장에서 올린 <strong>제조신청</strong>을 승인하면 그때 실제 제조가 실행됩니다.
          </>
        ) : (
          <>
            <strong>제조 신청</strong>을 올리면 영업지원팀 승인 후 처리됩니다 — 신청만으로는
            재고가 움직이지 않습니다.
          </>
        )}
      </div>

      {/* ── 제조신청 (대기·반려) ── */}
      {pendingRequests.length > 0 && (
        <div className="mb-5">
          <div className="text-sm font-semibold mb-2">
            제조신청{' '}
            <span className="text-slate-400 font-normal">({pendingRequests.length}건)</span>
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded">
            <table className="data-table w-full text-[13px]">
              <thead>
                {/* ⚠칸 폭 — 아래 제조 기록 표와 같은 기준으로 잡았다(실측 1920/1366/1280).
                    `w-*` 는 희망폭이라 효과가 없고, 줄바꿈을 막으려면 고정 성격 칸에 minWidth. */}
                <tr>
                  <th className="text-left whitespace-nowrap" style={{ minWidth: 118 }}>신청번호</th>
                  <th className="text-left whitespace-nowrap" style={{ minWidth: 92 }}>희망 제조일</th>
                  <th className="text-left" style={{ minWidth: 220 }}>만들 완제품</th>
                  <th className="text-right whitespace-nowrap" style={{ minWidth: 52 }}>수량</th>
                  <th className="text-left whitespace-nowrap" style={{ minWidth: 72 }}>원재료</th>
                  <th className="text-left hidden lg:table-cell whitespace-nowrap" style={{ minWidth: 70 }}>신청자</th>
                  <th className="text-left whitespace-nowrap" style={{ minWidth: 56 }}>상태</th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map((r) => {
                  const clickable = canApprove || r.requesterUserId === user?.userId;
                  return (
                    <tr
                      key={r.requestId}
                      onClick={() => handleRequestClick(r)}
                      className={clickable ? 'cursor-pointer hover:bg-slate-50' : ''}
                    >
                      <td className="font-medium text-blue-700 whitespace-nowrap">{r.requestNo}</td>
                      <td className="whitespace-nowrap">{r.requestDate}</td>
                      <td className="truncate">
                        {requestProductLabel(r)}
                        {r.newItem && (
                          <span className="ml-1 text-[11px] px-1 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-200">
                            새 품목
                          </span>
                        )}
                        {r.status === '반려' && r.rejectReason && (
                          <div className="text-xs text-rose-600 truncate">
                            반려 사유: {r.rejectReason}
                          </div>
                        )}
                      </td>
                      <td className="text-right num whitespace-nowrap">{r.producedQty}</td>
                      <td className="whitespace-nowrap text-slate-500">{r.items.length}종</td>
                      <td className="hidden lg:table-cell text-slate-500 whitespace-nowrap">
                        {r.requesterName ?? ''}
                      </td>
                      <td className="whitespace-nowrap">
                        {r.status === '반려' ? (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200">
                            반려
                          </span>
                        ) : (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                            대기
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── 제조 기록 ── */}
      {pendingRequests.length > 0 && (
        <div className="text-sm font-semibold mb-2">제조 기록</div>
      )}
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="data-table w-full text-[13px]">
          <thead>
            {/* ⚠칸 폭 — 실측(1920/1366/1280) 후 확정. `w-*` 는 희망폭이라 효과가 없고,
                줄바꿈을 막으려면 **고정 성격 칸에 minWidth** 를 줘야 한다.
                비고는 2xl(1536) 이상에서만 — 1366·1280 에서 켜 두면 제조번호·제조일이
                두 줄로 찌그러진다(측정값: 제조번호 76~85px, 행 높이 42→81px). */}
            <tr>
              <th className="text-left whitespace-nowrap" style={{ minWidth: 118 }}>제조번호</th>
              <th className="text-left whitespace-nowrap" style={{ minWidth: 92 }}>제조일</th>
              <th className="text-left" style={{ minWidth: 220 }}>완제품</th>
              <th className="text-right whitespace-nowrap" style={{ minWidth: 52 }}>수량</th>
              {!isViewer && (
                <th className="text-right whitespace-nowrap" style={{ minWidth: 100 }}>총원가</th>
              )}
              {!isViewer && (
                <th className="text-right whitespace-nowrap" style={{ minWidth: 108 }}>1개당 원가</th>
              )}
              <th className="text-left whitespace-nowrap" style={{ minWidth: 56 }}>상태</th>
              <th className="text-left hidden lg:table-cell whitespace-nowrap" style={{ minWidth: 70 }}>담당</th>
              <th className="text-left hidden 2xl:table-cell" style={{ minWidth: 140 }}>비고</th>
            </tr>
          </thead>
          <tbody>
            {listQ.isPending && (
              <tr>
                <td colSpan={isViewer ? 7 : 9} className="text-center text-slate-400 py-6"><LoadingLines /></td>
              </tr>
            )}
            {!listQ.isPending && rows.length === 0 && (
              <tr>
                <td colSpan={isViewer ? 7 : 9} className="text-center text-slate-400 py-6">
                  제조 기록이 없습니다.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.manufactureId}
                onClick={() => setDetailId(r.manufactureId)}
                className="cursor-pointer hover:bg-slate-50"
              >
                <td className="font-medium text-blue-700 whitespace-nowrap">{r.manufactureNo}</td>
                <td className="whitespace-nowrap">{r.manufactureDate}</td>
                <td className="truncate">
                  {r.itemName ?? r.itemCode}
                  {r.productOutDate && (
                    <span className="ml-1 text-[11px] px-1 py-0.5 rounded bg-slate-100 text-slate-500">
                      출고됨
                    </span>
                  )}
                </td>
                <td className="text-right num whitespace-nowrap">{r.producedQty}</td>
                {!isViewer && (
                  <td className="text-right num whitespace-nowrap">{money(r.totalCost)}</td>
                )}
                {!isViewer && (
                  <td className="text-right num whitespace-nowrap">{money(r.unitCost)}</td>
                )}
                <td className="whitespace-nowrap">
                  {r.status === '분해' ? (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200">
                      분해
                    </span>
                  ) : (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                      완료
                    </span>
                  )}
                </td>
                <td className="hidden lg:table-cell text-slate-500 whitespace-nowrap">
                  {r.requesterName ?? ''}
                </td>
                <td className="hidden 2xl:table-cell text-slate-500 truncate">{r.note ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Suspense fallback={null}>
        {creating && (
          <ManufactureFormModal
            mode={canApprove ? 'execute' : 'request'}
            onClose={() => setCreating(false)}
          />
        )}
        {openRequest && (
          <ManufactureFormModal
            mode={openRequest.mode}
            request={openRequest.row}
            onClose={() => setOpenRequest(null)}
          />
        )}
        {detailId != null && (
          <ManufactureDetailModal
            manufactureId={detailId}
            canEdit={canApprove}
            onClose={() => setDetailId(null)}
          />
        )}
      </Suspense>
    </div>
  );
}
